import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { feedsApi, ItemParams, ProposalParams, ProposalType, ThemePatch } from '../api/feedsApi'

const KEYS = {
  destinations: ['feeds-destinations'],
  items: (p: ItemParams) => ['feeds-items', p],
  sets: (destination: string) => ['feeds-sets', destination],
  themes: ['feeds-themes'],
}

const errorMessage = (e: unknown) => {
  const err = e as { response?: { data?: { message?: string; error?: string } }; message?: string }
  return err.response?.data?.message || err.response?.data?.error || err.message || 'Something went wrong'
}

/** Destinations poll every 10 s while one of them has a build running, so the card updates itself. */
export function useDestinations() {
  return useQuery({
    queryKey: KEYS.destinations,
    queryFn: feedsApi.getDestinations,
    staleTime: 30_000,
    refetchInterval: (q) => (q.state.data?.data.some((d) => d.builds[0]?.status === 'running') ? 10_000 : false),
  })
}

export function useBuildDestination() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ slug, skipSync }: { slug: string; skipSync: boolean }) => feedsApi.build(slug, skipSync),
    onSuccess: (r) => {
      toast({
        title: `Build started for ${r.destination}`,
        description: r.skipSync ? 'Re-rendering from the last Shopify sync.' : 'Syncing from Shopify, then pricing and rendering.',
      })
      // The feed_builds row is inserted a moment after the 202; refetch once it should exist.
      setTimeout(() => qc.invalidateQueries({ queryKey: KEYS.destinations }), 1500)
    },
    onError: (e) => toast({ title: 'Build not started', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useRotateToken() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: feedsApi.rotateToken,
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.destinations }),
    onError: (e) => toast({ title: 'Token not rotated', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useFeedItems(params: ItemParams) {
  return useQuery({
    queryKey: KEYS.items(params),
    queryFn: () => feedsApi.getItems(params),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  })
}

export function useMerchSets(destination = 'google-us') {
  return useQuery({ queryKey: KEYS.sets(destination), queryFn: () => feedsApi.getSets(destination), staleTime: 60_000 })
}

export function useMerchThemes() {
  return useQuery({ queryKey: KEYS.themes, queryFn: feedsApi.getThemes, staleTime: 60_000 })
}

export function useUpdateTheme() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ slug, patch }: { slug: string; patch: ThemePatch }) => feedsApi.updateTheme(slug, patch),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: KEYS.themes })
      qc.invalidateQueries({ queryKey: ['feeds-sets'] })
      toast({ title: `Saved ${t.name}`, description: 'Feed labels pick this up on the next build.' })
    },
    onError: (e) => toast({ title: 'Not saved', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useUpdateSet() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ slug, status }: { slug: string; status: 'draft' | 'retired' }) => feedsApi.updateSet(slug, status),
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ['feeds-sets'] })
      qc.invalidateQueries({ queryKey: KEYS.themes })
      toast({ title: `${s.slug} is now ${s.status}`, description: 'The feed reflects this on the next build.' })
    },
    onError: (e) => toast({ title: 'Not changed', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useProposals(params: ProposalParams) {
  return useQuery({
    queryKey: ['feeds-proposals', params],
    queryFn: () => feedsApi.getProposals(params),
    placeholderData: keepPreviousData,
    staleTime: 15_000,
  })
}

/** Approve or reject; approved set changes and negatives apply in the background, so refetch a little later too. */
export function useDecideProposals() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ type, ids, decision }: { type: ProposalType; ids: number[]; decision: 'approve' | 'reject' }) =>
      feedsApi.decideProposals(type, ids, decision),
    onSuccess: (r, v) => {
      qc.invalidateQueries({ queryKey: ['feeds-proposals'] })
      if (r.applying) setTimeout(() => qc.invalidateQueries({ queryKey: ['feeds-proposals'] }), 8000)
      toast({
        title: `${v.decision === 'approve' ? 'Approved' : 'Rejected'} ${r.decided}`,
        description: r.applying ? `Applying ${r.applying} in the background.` : undefined,
      })
    },
    onError: (e) => toast({ title: 'Not saved', description: errorMessage(e), variant: 'destructive' }),
  })
}
